import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { fetchPlacementCallRecording, } from '@/entities/queue-placement';
import { ApiError } from '@/shared/api';
import { Skeleton } from '@/shared/ui';
function formatClock(date) {
    return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
    });
}
function formatOccurredAt(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return iso;
    }
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}
function directionLabel(direction) {
    return direction === 'OUTBOUND' ? 'Исходящий' : 'Входящий';
}
function QueueCallRecordingPlayer({ placementId, session, }) {
    const [objectUrl, setObjectUrl] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [objectUrl]);
    if (session.recordingStatus === 'unavailable') {
        return (_jsx("p", { className: "text-xs text-muted-foreground", children: "\u0417\u0430\u043F\u0438\u0441\u0438 \u043D\u0435\u0442" }));
    }
    if (objectUrl) {
        return (_jsx("audio", { controls: true, className: "w-full", src: objectUrl, "aria-label": `Запись звонка ${session.entryId}` }));
    }
    return (_jsxs("div", { className: "space-y-1", children: [error ? (_jsx("p", { className: "text-xs text-destructive", children: error })) : null, _jsx("button", { type: "button", className: "text-sm text-primary underline-offset-2 hover:underline disabled:opacity-50", disabled: loading, onClick: () => {
                    setLoading(true);
                    setError(null);
                    void fetchPlacementCallRecording(placementId, session.entryId)
                        .then((blob) => {
                        setObjectUrl(URL.createObjectURL(blob));
                    })
                        .catch((err) => {
                        const message = err instanceof ApiError
                            ? err.message
                            : 'Не получилось открыть запись';
                        setError(message);
                    })
                        .finally(() => {
                        setLoading(false);
                    });
                }, children: loading ? 'Загружаем…' : 'Послушать' })] }));
}
function QueueCallTranscription({ session, }) {
    const transcription = session.transcription;
    if (!transcription) {
        return null;
    }
    const fixture = Boolean(transcription.text?.startsWith('Stub-расшифровка звонка'));
    if (transcription.status === 'queued' ||
        transcription.status === 'processing' ||
        fixture) {
        return (_jsx("p", { className: "text-xs text-muted-foreground", children: "\u0420\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u044B\u0432\u0430\u0435\u043C \u0440\u0430\u0437\u0433\u043E\u0432\u043E\u0440\u2026" }));
    }
    if (transcription.status === 'ready' && transcription.text) {
        return (_jsx("p", { className: "whitespace-pre-wrap text-sm text-foreground", children: transcription.text }));
    }
    if (transcription.status === 'failed') {
        return (_jsx("p", { className: "text-xs text-destructive", children: transcription.errorMessage ?? 'Не получилось расшифровать разговор' }));
    }
    return null;
}
export function QueueCallHistory({ placementId, calls, loading, refreshing = false, checkedAt = null, error, }) {
    const checkedLabel = checkedAt ? `проверено ${formatClock(checkedAt)}` : null;
    const summary = loading || (error && !checkedAt)
        ? 'Звонки'
        : refreshing
            ? `Звонки · ${calls.length} · проверяем новые`
            : checkedLabel
                ? `Звонки · ${calls.length} · ${checkedLabel}`
                : `Звонки · ${calls.length}`;
    return (_jsxs("details", { className: "border-t border-border pt-4", children: [_jsx("summary", { className: "cursor-pointer text-sm font-semibold", children: summary }), _jsxs("div", { className: "mt-2 space-y-2", children: [_jsx("p", { className: "text-xs text-muted-foreground", children: refreshing
                            ? 'Проверяем новые звонки в CRM.'
                            : checkedAt
                                ? `Новые звонки из CRM появляются здесь. Показаны последние 50. Проверено ${formatClock(checkedAt)}.`
                                : 'Пока карточка открыта, новые звонки из CRM появляются в этом списке.' }), loading ? (_jsxs("div", { className: "flex flex-col gap-2", "aria-busy": "true", children: [_jsx(Skeleton, { className: "h-16 w-full" }), _jsx(Skeleton, { className: "h-16 w-full" }), _jsx(Skeleton, { className: "h-16 w-full max-w-md" })] })) : null, error ? (_jsx("p", { className: "text-sm text-destructive", children: error })) : null, !loading && !error && calls.length === 0 ? (_jsx("p", { className: "text-sm text-muted-foreground", children: "\u0417\u0432\u043E\u043D\u043A\u043E\u0432 \u0441 \u044D\u0442\u0438\u043C \u0430\u0433\u0435\u043D\u0442\u043E\u043C \u043F\u043E\u043A\u0430 \u043D\u0435\u0442." })) : null, _jsx("ul", { className: "space-y-3", children: calls.map((session) => (_jsxs("li", { className: "rounded-md border border-border px-3 py-2", children: [_jsxs("p", { className: "text-sm font-medium", children: [directionLabel(session.direction), " \u00B7", ' ', formatOccurredAt(session.occurredAt)] }), _jsx("p", { className: "text-xs text-muted-foreground", children: session.missed
                                        ? 'Пропущен'
                                        : session.answered
                                            ? 'Отвечен'
                                            : 'Без ответа' }), _jsxs("div", { className: "mt-2 space-y-2", children: [_jsx(QueueCallRecordingPlayer, { placementId: placementId, session: session }), _jsx(QueueCallTranscription, { session: session })] })] }, session.entryId))) })] })] }));
}
